// @ts-ignore
import React, {
    createContext,
    PropsWithChildren,
    useContext,
    useState
} from "react";

import { useIntl } from "react-intl";

import FeatureGates from "@atlassian/feature-gate-js-client";

import {
    useFetchUserSegmentData,
    useSearchParameters,
    useUserSegmentationStatus
} from "./";

export type JiraTemplateChoice = "kanban" | "scrum" | "business" | undefined;

type JiraTemplateChoiceProps = {
    shouldShowJiraTemplateChoiceScreen: boolean;
    loadingUserSegmentData: boolean;
    templateChoice?: JiraTemplateChoice;
    setTemplateChoice: (templateChoice?: JiraTemplateChoice) => void;
    templateChoiceError: Error | undefined;
    setTemplateChoiceError: (e: Error) => void;
};

export const JiraTemplateChoiceContext = createContext<JiraTemplateChoiceProps>(
    {
        shouldShowJiraTemplateChoiceScreen: false,
        loadingUserSegmentData: false,
        templateChoice: undefined,
        setTemplateChoice: () => undefined,
        templateChoiceError: undefined,
        setTemplateChoiceError: () => undefined
    }
);

export const JiraTemplateChoiceWithFeatureFlagProvider = ({
    children
}: PropsWithChildren<unknown>) => {
    // PhoenixRises TODO: change to true when you want to test experiment
    const jiraTemplateChoiceExperimentEnabled = false;

    if (!jiraTemplateChoiceExperimentEnabled) {
        // Make this component skip it's behaviour
        return <>{children}</>;
    }

    return <JiraTemplateChoiceProvider>{children}</JiraTemplateChoiceProvider>;
};

const JiraTemplateChoiceProvider = ({
    children
}: PropsWithChildren<unknown>) => {
    const { products } = useSearchParameters();
    const { canShowJiraTemplateChoiceScreen, loadingUserSegmentData } =
        useCanShowJiraTemplateChoiceScreen(products);

    const [templateChoice, setTemplateChoice] = useState<
        JiraTemplateChoice | undefined
    >();

    const [templateChoiceError, setTemplateChoiceError] = useState<
        Error | undefined
    >();

    const userHasPickedTemplate = templateChoice !== undefined;

    const shouldShowJiraTemplateChoiceScreen =
        templateChoiceError === undefined &&
        canShowJiraTemplateChoiceScreen &&
        !userHasPickedTemplate;

    return (
        <JiraTemplateChoiceContext.Provider
            value={{
                templateChoice,
                loadingUserSegmentData,
                setTemplateChoice,
                shouldShowJiraTemplateChoiceScreen,
                templateChoiceError,
                setTemplateChoiceError
            }}
        >
            {children}
        </JiraTemplateChoiceContext.Provider>
    );
};

/* The user must pass the following conditions to see the template choice screen
   - locale: The user locale should be 'en' since this experiment will only run for the EN locale due to a
     short experiment lead time, factoring out translations to other locales
   - !hasTemplateRecommendation: Supplied via a query param, there must be no `templateId`
   - isProvisioningOnlyJSW: Provisioning only JSW and no other products
   - isProvisioningUserSegProducts: Is provisioning user segmentation products (which should be true if only JSW)
   - !isUserSegHiddenByQueryParam: User does not have `hideUserSegmentation=true` as a query param
   - !alreadyViewedUserSegmentationScreen: User is seeing segmentation screen for the first time. The
     user segmentation screen makes an API call to determine if the user has already seen the screen
     in the last 365 days. If they have seen it, the user seg screen is not displayed, and we do not meet
     our requirements to show the TTC either.
   - !isBusinessSegmentUser: The user must not be counted as a business user by user segmentation
   - !fetchError: This hook must access /gateway/api/me to check user segmentation and this call must succeed
     in order for the check above to be valid
 */
export const useCanShowJiraTemplateChoiceScreen = (
    products: readonly string[]
) => {
    const {
        isProvisioningUserSegProducts,
        isUserSegHiddenByQueryParam,
        alreadyViewedUserSegmentationScreen,
        userSegmentationPageIsFinished
    } = useUserSegmentationStatus(products);

    const { locale } = useIntl();
    const hasTemplateRecommendation = Boolean(useSearchParameters().templateId);
    // don't make call to bxpkit-core for product key
    // caused build error
    const isProvisioningOnlyJSW =
        products.length === 1 && products[0] === "jira-software.ondemand";
    const exposedToUserSegScreen =
        isProvisioningUserSegProducts && !isUserSegHiddenByQueryParam;

    const canShowTTCPreFetchConditions =
        locale === "en" &&
        !hasTemplateRecommendation &&
        isProvisioningOnlyJSW &&
        exposedToUserSegScreen &&
        !alreadyViewedUserSegmentationScreen;

    // Hides user segmentation screens if user is in experiment EXPCEN-4467
    const isUserInNoUserSegAndtemplateCopyUpdatesVariant =
        FeatureGates.getExperimentValue(
            "jsw_signup_remove_user_segmentation_screens",
            "noUserSegAndtemplateCopyUpdates",
            false
        );

    const { fetched, fetchError, isBusinessSegmentUser } =
        useFetchUserSegmentData(
            canShowTTCPreFetchConditions &&
                (userSegmentationPageIsFinished ||
                    isUserInNoUserSegAndtemplateCopyUpdatesVariant)
        );

    return {
        canShowJiraTemplateChoiceScreen:
            canShowTTCPreFetchConditions &&
            !isBusinessSegmentUser &&
            !fetchError,
        loadingUserSegmentData: !fetched
    };
};

export const useJiraTemplateChoice = () => {
    return useContext(JiraTemplateChoiceContext);
};
